'use es6';

import RecordPropType from 'conversations-prop-types/prop-types/RecordPropType';
import { isCommonMessageFormat, isEmailCMF } from 'conversations-message-history/common-message-format/operators/cmfComparators';
import { getSenderId, getSenderType, getMessageSendFailure } from 'conversations-message-history/common-message-format/operators/commonMessageFormatGetters';
import { getFromSenderName, getOriginalFromSenderEmail, getGenericChannelId } from 'conversations-message-history/common-message-format/operators/commonMessageGetters';
import { isThreadComment } from 'conversations-message-history/comment-message/operators/cmComparators';
import { getMessage, getAllowRetry } from 'conversations-message-history/unpublished-messages/operators/getters';
import { List, Set as ImmutableSet } from 'immutable';
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import styled from 'styled-components';
import { isSenderPresent } from 'conversations-message-history/senders/operators/isSenderPresent';
import { getEmailMetadata } from 'conversations-message-history/common-message-format/operators/getEmailMetadata';
import { getTotalRecipientsCount } from '../common-message/operators/getTotalRecipientsCount';
import CommentMessageBody from '../comment/CommentMessageBody';
import { AGENT } from 'conversations-message-history/constants/messageUses';
import { COMMENT } from 'conversations-message-history/constants/tileMessageUses';
import PrimaryMessageContentSection from '../primary-message/components/PrimaryMessageContentSection';
import PositionDescriptors from 'conversations-message-history/proptypes/PositionDescriptors';
import SenderMap from 'conversations-message-history/proptypes/SenderMap';
import { findSender } from 'conversations-message-history/senders/operators/findSender';
import { getData } from 'conversations-message-history/senders/operators/getters';
import { isSenderLoading } from 'conversations-message-history/senders/operators/isSenderLoading';
import { senderEmailDoesNotMatchVisitor } from 'conversations-message-history/senders/operators/senderEmailDoesNotMatchVisitor';
import Sender from 'conversations-message-history/senders/records/Sender';
// @ts-expect-error module not typed
import { getSenderIdOrVid } from 'conversations-message-history/common-message-format/operators/getSenderIdOrVid';
import TileMessage from '../tile-message/TileMessage';
import FailedToPublishMessageText from './FailedToPublishMessageText';
import CommonMessageContent from '../common-message/CommonMessageContent';
import CommonMessageSection from './CommonMessageSection';
import CommonAttachmentsFooter from './CommonAttachmentsFooter';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { getMessageRecipientNames } from '../common-message/operators/getMessageRecipientNames';
import RecipientLine from '../common-message/RecipientLine';
import SlottedMessage from '../slotted-message/SlottedMessage';
import FullSenderName from '../senders/components/FullSenderName';
import CommonMessageHeaderPopover from '../common-message-popover/CommonMessageHeaderPopover';
import { ChannelIcon } from '../atoms/ChannelIcon';
import { jsx as _jsx } from "react/jsx-runtime";
const FailedHeaderWrapper = styled.div.withConfig({
  displayName: "FailedToPublishMessage__FailedHeaderWrapper",
  componentId: "c7bbmg-0"
})(["line-height:24px;"]);
const FailedToPublishMessageBody = ({
  genericChannelId,
  message,
  fileAttachments,
  renderMessageContent
}) => /*#__PURE__*/_jsx(PrimaryMessageContentSection, {
  canExpand: true,
  genericChannelId: genericChannelId,
  isLastMessage: true,
  children: /*#__PURE__*/_jsx(CommonMessageSection, {
    message: message,
    fileAttachments: fileAttachments,
    renderMessageContent: renderMessageContent
  })
});
FailedToPublishMessageBody.displayName = 'FailedToPublishMessageBody';
FailedToPublishMessageBody.propTypes = {
  fileAttachments: PropTypes.instanceOf(List),
  genericChannelId: PropTypes.number,
  message: RecordPropType('CommonMessage').isRequired,
  renderMessageContent: PropTypes.func.isRequired
};
const FailedToPublishMessageFooter = props => /*#__PURE__*/_jsx(PrimaryMessageContentSection, {
  children: /*#__PURE__*/_jsx(CommonAttachmentsFooter, Object.assign({}, props))
});
FailedToPublishMessageFooter.displayName = 'FailedToPublishMessageFooter';
class FailedToPublishMessage extends PureComponent {
  constructor(...args) {
    super(...args);
    this.renderPrimaryMessageContent = () => {
      const {
        fetchReplies,
        message: failedToPublishMessage,
        messageReplyIsLoading,
        plainTextMode
      } = this.props;
      const message = getMessage(failedToPublishMessage);
      return /*#__PURE__*/_jsx(CommonMessageContent, {
        fetchReplies: fetchReplies,
        message: message,
        messageReplyIsLoading: messageReplyIsLoading,
        plainTextMode: plainTextMode
      });
    };
    this.renderFailedHeader = () => {
      const {
        message: failedToPublishMessage,
        onRepublish
      } = this.props;
      const message = getMessage(failedToPublishMessage);
      return (
        /*#__PURE__*/
        // @bend-expect trellis-tokenized
        _jsx(FailedHeaderWrapper, {
          children: /*#__PURE__*/_jsx(FailedToPublishMessageText, {
            message: message,
            onRepublish: onRepublish
          })
        })
      );
    };
  }
  render() {
    const {
      channelName,
      connectedAccounts,
      fileAttachments,
      friendlyNames,
      message: failedToPublishMessage,
      onClickAvatar,
      onRepublish,
      positionDescriptors,
      senders,
      senderName,
      vid
    } = this.props;
    const message = getMessage(failedToPublishMessage);
    const commonMessageFormat = isCommonMessageFormat(message);
    const genericChannelId = getGenericChannelId(message);
    if (commonMessageFormat) {
      const recipientNames = getMessageRecipientNames({
        message,
        friendlyNames
      });
      const senderId = getSenderId(message);
      const updatedSenderId = getSenderIdOrVid({
        message,
        vid,
        senderId
      });
      const senderType = getSenderType(message);
      const asyncSender = findSender(updatedSenderId, senderType, senders);
      const present = isSenderPresent(updatedSenderId, senderType, ImmutableSet());
      let sender = getData(asyncSender);
      if (!sender || senderEmailDoesNotMatchVisitor({
        sender,
        message
      })) {
        sender = Sender({
          senderType,
          firstName: getFromSenderName(message),
          email: getOriginalFromSenderEmail(message) || null
        });
      }
      const isEmailMessage = isCommonMessageFormat(message) && Boolean(getEmailMetadata(message));
      const totalRecipients = isEmailMessage ? getTotalRecipientsCount({
        message
      }) : 0;
      const isLoading = isSenderLoading(asyncSender);
      const sendFailure = getMessageSendFailure(message);
      const messageRetryable = sendFailure ? sendFailure.get('retryable') : false;
      const shouldDisplayByline = isEmailCMF(message);
      const allowRetry = getAllowRetry(failedToPublishMessage) && messageRetryable;
      return /*#__PURE__*/_jsx(SlottedMessage, {
        container: {
          componentProps: {
            positionDescriptors,
            use: AGENT
          }
        },
        avatar: {
          isLoading,
          componentProps: {
            onClick: onClickAvatar,
            sender,
            present,
            size: 'sm'
          }
        },
        title: {
          isLoading,
          loaderProps: {
            use: 'default'
          },
          Component: FullSenderName,
          componentProps: {
            sender,
            senderName,
            'data-test-id': 'sender-name'
          }
        },
        detail: totalRecipients > 1 ? {
          Component: FormattedMessage,
          componentProps: {
            'data-test-id': 'email-header-recipient-count',
            message: 'conversations-inbox-message-history.email.messageHeaderRecipientsCount',
            options: {
              count: totalRecipients
            }
          }
        } : null,
        channel: {
          renderCustom: () => channelName || /*#__PURE__*/_jsx(ChannelIcon, {
            genericChannelId: genericChannelId
          })
        },
        detailsPopover: {
          Component: CommonMessageHeaderPopover,
          componentProps: {
            message,
            sender,
            isSenderLoading: isLoading
          }
        },
        byline: shouldDisplayByline ? {
          Component: RecipientLine,
          componentProps: {
            recipientNames
          }
        } : null,
        body: {
          Component: FailedToPublishMessageBody,
          componentProps: {
            genericChannelId,
            message,
            fileAttachments,
            renderMessageContent: this.renderPrimaryMessageContent
          }
        },
        footerBody: {
          Component: FailedToPublishMessageFooter,
          componentProps: {
            allowRetry,
            message,
            onRepublish,
            connectedAccounts,
            fileAttachments,
            'data-test-id': 'file-attachments-footer'
          }
        },
        annotation: {
          Component: FailedToPublishMessageText,
          componentProps: {
            allowRetry,
            message,
            onRepublish,
            connectedAccounts
          }
        }
      });
    }
    if (isThreadComment(message)) {
      return /*#__PURE__*/_jsx(TileMessage, {
        use: COMMENT,
        positionDescriptors: positionDescriptors,
        children: /*#__PURE__*/_jsx(CommentMessageBody, {
          message: message,
          onClickAvatar: onClickAvatar,
          senders: senders,
          renderHeaderMetadata: this.renderFailedHeader
        })
      });
    }
    return null;
  }
}
FailedToPublishMessage.displayName = 'FailedToPublishMessage';
FailedToPublishMessage.defaultProps = {
  plainTextMode: false
};
FailedToPublishMessage.propTypes = {
  channelName: PropTypes.string,
  connectedAccounts: PropTypes.object.isRequired,
  fetchReplies: PropTypes.func.isRequired,
  fileAttachments: PropTypes.instanceOf(List),
  friendlyNames: PropTypes.any,
  // refer to the TS type instead
  message: RecordPropType('FailedToPublish').isRequired,
  messageReplyIsLoading: PropTypes.bool.isRequired,
  onClickAvatar: PropTypes.func,
  onRepublish: PropTypes.func.isRequired,
  plainTextMode: PropTypes.bool.isRequired,
  positionDescriptors: PositionDescriptors.isRequired,
  senderName: PropTypes.node,
  senders: SenderMap,
  vid: PropTypes.number
};
export default FailedToPublishMessage;